// <copyright file="DleGeneralInfo.tsx">
// © 2024 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import React from "react";
import styles from "src/components/DataLakeExplorer/DleGeneralInfo.module.css";
import type { FC } from "react";

// Properties to initialize this component
interface DleGeneralInfoProps {}

const DleGeneralInfo: FC<DleGeneralInfoProps> = (
    props: DleGeneralInfoProps
) => {
    console.debug("DleGeneralInfo props: ", props);
    return (
        <div className={styles.DleGeneralInfo} data-testid="DleGeneralInfo">
            <div className="flex flex-column gap-3 mt-2 mb-2">
                <label
                    htmlFor="data-lake-explorer-overview"
                    className="mr-2 text-xl"
                >
                    Data Lake Overview
                </label>
                <div
                    id="data-lake-explorer-overview"
                    title="data-lake-explorer-overview"
                >
                    The V2X DEP data lake is a collection of curated historical
                    datasets, most of which are updated continuously as data is
                    ingested into the platform. Information is curated to a
                    common data format with metadata added for retrieval and
                    filtering.
                    <br />
                    <br />
                    Other transformations are also applied, such as splitting
                    batch-ingested records into individually accessible records
                    per entity. Curated data is also partitioned, cataloged, and
                    indexed for efficient storage and retrieval via supported
                    query engines.
                </div>
                <label
                    htmlFor="data-lake-explorer-format"
                    className="mr-2 text-xl"
                >
                    Common Data Format
                </label>
                <div
                    id="data-lake-explorer-format"
                    title="data-lake-explorer-format"
                >
                    Curated records are stored in AWS S3 as bulk (multi-record)
                    Parquet files written using dataset-specific Avro schemas in
                    the schema registry.
                    <br />
                    <br />
                    Metadata is added to all curated records to facilitate
                    filtering data by sub-type, time, location, and source.
                    <br />
                    <br />
                    Metadata is also added to raw data (pre-curation), which is
                    stored but not accessible to data lake users. Raw data may
                    be made available by request.
                </div>
                <label
                    htmlFor="data-lake-explorer-catalog"
                    className="mr-2 text-xl"
                >
                    Data Catalog
                </label>
                <div
                    id="data-lake-explorer-catalog"
                    title="data-lake-explorer-catalog"
                >
                    The AWS Glue Data Catalog stores metadata about the data
                    lake, which can be used by query engines for efficient data
                    retrieval. Metadata includes dataset names, schemas, file
                    storage locations, data formats and compression, data
                    partitions and indexes.
                    <br />
                    <br />
                    Data catalog tables are created and versioned using Avro
                    schemas in the schema registry. Data partitions and indexes
                    are updated regularly as data is ingested.
                </div>
                <label
                    htmlFor="data-lake-explorer-registry"
                    className="mr-2 text-xl"
                >
                    Schema Registry
                </label>
                <div
                    id="data-lake-explorer-registry"
                    title="data-lake-explorer-registry"
                >
                    The AWS Glue Schema Registry stores and versions Avro
                    schemas used to curate data.
                </div>
                <label
                    htmlFor="data-lake-explorer-query"
                    className="mr-2 text-xl"
                >
                    Query Engine
                </label>
                <div
                    id="data-lake-explorer-query"
                    title="data-lake-explorer-query"
                >
                    The AWS Athena Query Editor web console is available to DEP
                    users to find, view, and export data using the Athena query
                    engine. This engine uses the data catalog and the schema
                    registry.
                    <br />
                    <br />
                    See curated record type information for detailed guidance
                    and examples on leveraging these data lake components to
                    query specific datasets.
                </div>
            </div>
        </div>
    );
};

export default DleGeneralInfo;
